/** Mirrors portal ThemeConfig subset used by purchase portal theme. */
export interface WidgetPurchasePortalThemeConfig {
  primary_color?: string;
  secondary_color?: string;
  header_background_color?: string;
  background_color?: string;
  text_color?: string;
  muted_text_color?: string;
  primary_surface_color?: string;
  secondary_surface_color?: string;
  success_color?: string;
  warning_color?: string;
  error_color?: string;
  font_family?: string;
  border_radius?: string;
  theme_mode?: 'light' | 'dark' | 'system';
  spacing_scale?: 'compact' | 'comfortable';
  input_shape?: 'round' | 'pill' | 'square';
  button_style?: 'filled' | 'outlined' | 'soft';
}

export interface PurchasePortalThemeColors {
  text_color?: string;
  error_color?: string;
  primary_color?: string;
  header_background_color?: string;
  success_color?: string;
  primary_surface_color?: string;
  secondary_surface_color?: string;
  warning_color?: string;
  secondary_color?: string;
  background_color?: string;
  muted_text_color?: string;
}

export interface PurchasePortalTheme {
  theme_mode?: 'light' | 'dark' | 'system';
  colors_dark?: PurchasePortalThemeColors;
  colors_light?: PurchasePortalThemeColors;
  typography?: { font_family?: string };
  shape?: {
    border_radius?: string;
    input_shape?: 'round' | 'pill' | 'square';
    button_style?: 'filled' | 'outlined' | 'soft';
  };
  ux?: { spacing_scale?: 'compact' | 'comfortable' };
}

const DEFAULT_THEME: WidgetPurchasePortalThemeConfig = {
  primary_color: '#007BFF',
  secondary_color: '#F6F2F7',
  header_background_color: '#007BFF',
  background_color: '#ffffff',
  text_color: '#000000',
  muted_text_color: '#7A7A7A',
  primary_surface_color: '#fafafa',
  secondary_surface_color: '#E9EcEF',
  success_color: '#16a34a',
  warning_color: '#eab308',
  error_color: '#dc2626',
  font_family: 'Inter, sans-serif',
  border_radius: '8px',
  theme_mode: 'light',
  spacing_scale: 'comfortable',
};

export function parsePurchasePortalTheme(
  theme: string | PurchasePortalTheme | null | undefined,
): PurchasePortalTheme | null {
  if (!theme) {
    return null;
  }

  if (typeof theme === 'object') {
    return theme;
  }

  try {
    return JSON.parse(theme) as PurchasePortalTheme;
  } catch {
    return null;
  }
}

function ensureHashPrefix(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

function shouldUseDarkMode(themeMode: string): boolean {
  switch (themeMode) {
    case 'dark':
      return true;
    case 'light':
      return false;
    case 'system':
    default:
      if (
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ) {
        return true;
      }
      return false;
  }
}

function mergeCompanyColors(
  merged: WidgetPurchasePortalThemeConfig,
  companyTheme: PurchasePortalTheme,
  isDarkMode: boolean,
): void {
  const colors = isDarkMode
    ? companyTheme.colors_dark
    : companyTheme.colors_light;
  if (!colors) {
    return;
  }

  const colorMappings: Array<
    [keyof PurchasePortalThemeColors, keyof WidgetPurchasePortalThemeConfig]
  > = [
    ['primary_color', 'primary_color'],
    ['secondary_color', 'secondary_color'],
    ['header_background_color', 'header_background_color'],
    ['background_color', 'background_color'],
    ['text_color', 'text_color'],
    ['muted_text_color', 'muted_text_color'],
    ['primary_surface_color', 'primary_surface_color'],
    ['secondary_surface_color', 'secondary_surface_color'],
    ['success_color', 'success_color'],
    ['warning_color', 'warning_color'],
    ['error_color', 'error_color'],
  ];

  colorMappings.forEach(([sourceKey, targetKey]) => {
    const value = colors[sourceKey];
    if (value) {
      (merged as Record<string, string>)[targetKey] = value;
    }
  });
}

function mergeCompanyTheme(
  baseTheme: WidgetPurchasePortalThemeConfig,
  companyTheme: PurchasePortalTheme,
  isDarkMode: boolean,
): WidgetPurchasePortalThemeConfig {
  const merged = { ...baseTheme };

  mergeCompanyColors(merged, companyTheme, isDarkMode);

  if (companyTheme.typography?.font_family) {
    merged.font_family = companyTheme.typography.font_family;
  }

  if (companyTheme.shape?.border_radius) {
    merged.border_radius = `${companyTheme.shape.border_radius}px`;
  }
  if (companyTheme.shape?.input_shape) {
    merged.input_shape = companyTheme.shape.input_shape;
  }
  if (companyTheme.shape?.button_style) {
    merged.button_style = companyTheme.shape.button_style;
  }

  if (companyTheme.theme_mode) {
    merged.theme_mode = companyTheme.theme_mode;
  }
  if (companyTheme.ux?.spacing_scale) {
    merged.spacing_scale = companyTheme.ux.spacing_scale;
  }

  return merged;
}

function mergeQueryParams(
  baseTheme: WidgetPurchasePortalThemeConfig,
  queryParams: Record<string, string>,
): WidgetPurchasePortalThemeConfig {
  const merged = { ...baseTheme };

  const colorParams: Array<[string, keyof WidgetPurchasePortalThemeConfig]> = [
    ['primary-color', 'primary_color'],
    ['secondary-color', 'secondary_color'],
    ['header-background-color', 'header_background_color'],
    ['background-color', 'background_color'],
    ['text-color', 'text_color'],
    ['muted-text-color', 'muted_text_color'],
    ['primary-surface-color', 'primary_surface_color'],
    ['secondary-surface-color', 'secondary_surface_color'],
    ['success-color', 'success_color'],
    ['warning-color', 'warning_color'],
    ['error-color', 'error_color'],
  ];

  colorParams.forEach(([paramKey, themeKey]) => {
    const value = queryParams[paramKey];
    if (value) {
      (merged as Record<string, string>)[themeKey] = ensureHashPrefix(value);
    }
  });

  if (queryParams['font-family']) {
    merged.font_family = queryParams['font-family'];
  }
  if (queryParams['border-radius']) {
    merged.border_radius = `${queryParams['border-radius']}px`;
  }
  if (queryParams['theme-mode']) {
    merged.theme_mode = queryParams['theme-mode'] as WidgetPurchasePortalThemeConfig['theme_mode'];
  }
  if (queryParams['theme']) {
    merged.theme_mode = queryParams['theme'] as WidgetPurchasePortalThemeConfig['theme_mode'];
  }

  return merged;
}

/** Same priority as portal ThemeService: query params > company API > defaults. */
export function buildPurchasePortalTheme(
  companyTheme?: PurchasePortalTheme | null,
  queryParams: Record<string, string> = {},
  isDarkMode = false,
): { theme: WidgetPurchasePortalThemeConfig; isDark: boolean } {
  let theme: WidgetPurchasePortalThemeConfig = { ...DEFAULT_THEME };

  if (companyTheme) {
    theme = mergeCompanyTheme(theme, companyTheme, isDarkMode);
  }

  theme = mergeQueryParams(theme, queryParams);

  const isDark = shouldUseDarkMode(theme.theme_mode ?? 'light');

  return { theme, isDark };
}

export function applyPurchasePortalThemeToHost(
  host: HTMLElement,
  theme: WidgetPurchasePortalThemeConfig,
  isDark: boolean,
): void {
  host.setAttribute('data-theme', isDark ? 'dark' : 'light');

  const colorMappings: Array<[keyof WidgetPurchasePortalThemeConfig, string]> = [
    ['primary_color', '--ufp-primary-color'],
    ['secondary_color', '--ufp-secondary-color'],
    ['header_background_color', '--ufp-header-background-color'],
    ['background_color', '--ufp-background-color'],
    ['text_color', '--ufp-text-color'],
    ['muted_text_color', '--ufp-muted-text-color'],
    ['primary_surface_color', '--ufp-primary-surface-color'],
    ['secondary_surface_color', '--ufp-secondary-surface-color'],
    ['success_color', '--ufp-success-color'],
    ['warning_color', '--ufp-warning-color'],
    ['error_color', '--ufp-error-color'],
  ];

  colorMappings.forEach(([themeKey, cssVar]) => {
    const value = theme[themeKey];
    if (value) {
      host.style.setProperty(cssVar, String(value));
    }
  });

  if (theme.font_family) {
    host.style.setProperty('--ufp-font-family', theme.font_family);
  }

  if (theme.border_radius) {
    host.style.setProperty('--ufp-border-radius', theme.border_radius);
  }
}

export function applyDefaultPurchasePortalTheme(host: HTMLElement): void {
  const { theme, isDark } = buildPurchasePortalTheme();
  applyPurchasePortalThemeToHost(host, theme, isDark);
}
